import React, { useState } from "react";
import { createPortal } from "react-dom";
import { MessageSquarePlus, X, Send } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const close = () => { setOpen(false); setMessage(""); };

  const submit = async () => {
    const msg = message.trim();
    if (!msg) return;
    setSending(true);
    try {
      await api.post("/feedback", { message: msg });
      toast.success("Thanks! Your feedback reached our team.");
      setMessage("");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send feedback");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="feedback-cta">
      <button onClick={() => setOpen(true)} data-testid="feedback-open-btn"
        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-accent/40 hover:bg-accent/70 transition text-left">
        <span className="h-8 w-8 rounded-lg bg-indigo-500/20 text-indigo-500 flex items-center justify-center shrink-0"><MessageSquarePlus className="h-4 w-4" /></span>
        <span className="min-w-0">
          <span className="block text-xs font-semibold">Send feedback</span>
          <span className="block text-[10px] text-muted-foreground">Ideas, bugs or a hello</span>
        </span>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="feedback-modal">
          <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-card border border-border rounded-2xl p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-lg">Send us feedback</div>
                <p className="text-xs text-muted-foreground mt-0.5">Tell us what's working, what's not, or what you'd love to see.</p>
              </div>
              <button onClick={close} className="p-1 rounded-md hover:bg-accent text-muted-foreground"><X className="h-4 w-4" /></button>
            </div>
            <textarea value={message} onChange={e => setMessage(e.target.value)} autoFocus rows={5}
              data-testid="feedback-message"
              placeholder="Your message…"
              className="w-full px-3 py-2.5 rounded-lg bg-background border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
            <div className="flex justify-end gap-2">
              <button onClick={close} className="px-4 py-2 rounded-lg border border-border text-sm">Cancel</button>
              <button onClick={submit} disabled={sending || !message.trim()} data-testid="feedback-submit-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 disabled:opacity-50">
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
